/** @module @liquidefy/code-editor/circuits/adaptive-workspace @version 1.0.0 @license LQD Research LLC */
/** @rust adaptive_workspace.rs — false; 4/2/26; sm */

/**
 * Tier 4 — Better Than Existing: Workspace That Learns
 *
 * Combines workspace state, focus mode, theme adaptation,
 * glassmorphism backdrop, AI learning/feedback, personalization,
 * and onboarding into a workspace that adapts to the user over time.
 *
 * Pure circuit. No DOM, no framework coupling.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  WorkspaceState,
  EditorResult,
  PersonalizationState,
  SkillLevel,
} from '../code-editor-types.js';

import { ok, err, generateId, now } from '../code-editor.js';
import { createWorkspace } from '../workspace/workspace.js';
import type { FocusIntensity, FocusThemeState } from '../themes/adaptive/focus.js';
import { createFocusThemeState, enterFocusMode, exitFocusMode } from '../themes/adaptive/focus.js';
import { createDefaultDarkGround, createDefaultLightGround } from '../themes/glass/backdrop.js';
import type { CodingEvent } from '../ai/agents/personalization.js';
import { createPersonalizationState, recordCodingEvent, getCompetencyLevel, getPersonalizedSuggestions } from '../ai/agents/personalization.js';
import { createFeedbackState, recordFeedback as feedbackRecord, adjustConfidence } from '../ai/learning/feedback.js';
import { createAdaptationState, adjustPromptForUser } from '../ai/learning/adaptation.js';
import type { Tutorial } from '../terminal/welcome/onboarding.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface AdaptiveWorkspaceConfig {
  readonly userId: string;
  readonly workspaceName: string;
  readonly theme?: 'dark' | 'light';
  readonly learningRate?: number;
}

export interface StyleProfile {
  readonly preferredIndentation: 'tabs' | 'spaces';
  readonly indentSize: number;
  readonly prefersSemicolons: boolean;
  readonly prefersTrailingCommas: boolean;
  readonly averageLineLength: number;
  readonly preferredNamingConvention: 'camelCase' | 'snake_case' | 'PascalCase' | 'mixed';
}

export interface AdaptedSettings {
  readonly confidenceFloor: number;
  readonly verbosity: 'minimal' | 'standard' | 'verbose';
  readonly preferredModel: string | null;
}

export interface SkillAssessment {
  readonly level: SkillLevel;
  readonly commandsUsed: number;
  readonly uniqueCommandsUsed: number;
  readonly conceptsLearned: string[];
  readonly lastAssessedAt: number;
}

export interface AdaptiveWorkspaceHandle extends CircuitHandle {
  getWorkspace(): WorkspaceState;
  recordActivity(action: string): void;
  getSkillAssessment(): SkillAssessment;
  enterFocusMode(intensity?: FocusIntensity): void;
  exitFocusMode(): void;
  recordFeedback(signal: 'accepted' | 'rejected', requestType: string): void;
  getAdaptedSettings(): AdaptedSettings;
  learnFromCode(code: string, language: string): void;
  getStyleProfile(): StyleProfile;
  getRecentActivity(): string[];
  suggestNextTutorial(): Tutorial | null;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 STYLE ANALYSIS ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

interface StyleAccumulator {
  tabCount: number;
  spaceCount: number;
  indentSizes: number[];
  semiCount: number;
  noSemiCount: number;
  trailingCommaCount: number;
  noTrailingCommaCount: number;
  lineLengths: number[];
  camelCaseCount: number;
  snakeCaseCount: number;
  pascalCaseCount: number;
}

function createStyleAccumulator(): StyleAccumulator {
  return {
    tabCount: 0, spaceCount: 0, indentSizes: [], semiCount: 0, noSemiCount: 0,
    trailingCommaCount: 0, noTrailingCommaCount: 0, lineLengths: [],
    camelCaseCount: 0, snakeCaseCount: 0, pascalCaseCount: 0,
  };
}

function analyzeCodeStyle(accumulator: StyleAccumulator, code: string): void {
  const lines = code.split('\n');
  for (const line of lines) {
    if (!line.trim()) continue;
    accumulator.lineLengths.push(line.length);

    // Indentation
    const leadingWhitespace = line.match(/^(\s+)/);
    if (leadingWhitespace) {
      const ws = leadingWhitespace[1]!;
      if (ws.includes('\t')) accumulator.tabCount++;
      else {
        accumulator.spaceCount++;
        accumulator.indentSizes.push(ws.length);
      }
    }

    // Semicolons (JS/TS heuristic)
    const trimmed = line.trim();
    if (trimmed.length > 5 && !trimmed.startsWith('//') && !trimmed.startsWith('/*') && !trimmed.startsWith('*')) {
      if (trimmed.endsWith(';')) accumulator.semiCount++;
      else if (/^(const|let|var|return|import|export)\b/.test(trimmed) && !trimmed.endsWith('{') && !trimmed.endsWith(',')) {
        accumulator.noSemiCount++;
      }
    }

    // Trailing commas
    if (trimmed.endsWith(',')) accumulator.trailingCommaCount++;

    // Naming conventions — scan identifiers
    const identifiers = trimmed.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) ?? [];
    for (const id of identifiers) {
      if (id.length < 3) continue;
      if (/^[a-z][a-zA-Z0-9]*$/.test(id) && /[A-Z]/.test(id)) accumulator.camelCaseCount++;
      else if (/^[a-z][a-z0-9_]*$/.test(id) && id.includes('_')) accumulator.snakeCaseCount++;
      else if (/^[A-Z][a-zA-Z0-9]*$/.test(id)) accumulator.pascalCaseCount++;
    }
  }
}

function computeStyleProfile(accumulator: StyleAccumulator): StyleProfile {
  const avgLineLength = accumulator.lineLengths.length > 0
    ? Math.round(accumulator.lineLengths.reduce((a, b) => a + b, 0) / accumulator.lineLengths.length)
    : 80;

  const mostCommonIndent = accumulator.indentSizes.length > 0
    ? mode(accumulator.indentSizes)
    : 2;

  let namingConvention: StyleProfile['preferredNamingConvention'] = 'mixed';
  const maxNaming = Math.max(accumulator.camelCaseCount, accumulator.snakeCaseCount, accumulator.pascalCaseCount);
  if (maxNaming > 0) {
    if (accumulator.camelCaseCount === maxNaming) namingConvention = 'camelCase';
    else if (accumulator.snakeCaseCount === maxNaming) namingConvention = 'snake_case';
    else if (accumulator.pascalCaseCount === maxNaming) namingConvention = 'PascalCase';
  }

  return {
    preferredIndentation: accumulator.tabCount > accumulator.spaceCount ? 'tabs' : 'spaces',
    indentSize: mostCommonIndent,
    prefersSemicolons: accumulator.semiCount > accumulator.noSemiCount,
    prefersTrailingCommas: accumulator.trailingCommaCount > accumulator.noTrailingCommaCount,
    averageLineLength: avgLineLength,
    preferredNamingConvention: namingConvention,
  };
}

function mode(numbers: number[]): number {
  const freq = new Map<number, number>();
  let maxCount = 0;
  let maxVal = numbers[0]!;
  for (const n of numbers) {
    const count = (freq.get(n) ?? 0) + 1;
    freq.set(n, count);
    if (count > maxCount) { maxCount = count; maxVal = n; }
  }
  return maxVal;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 TUTORIAL SUGGESTION ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

const TUTORIAL_LIBRARY: Tutorial[] = [
  { id: 'basics-navigation', title: 'File Navigation Basics', description: 'Learn to move between files efficiently', category: 'navigation', requiredLevel: 'beginner' as SkillLevel, steps: [{ instruction: 'Use cd to change directories', hint: 'Try cd ..', explanation: 'cd changes your current working directory', expectedCommand: 'cd' }], estimatedMinutes: 5 },
  { id: 'basics-file-ops', title: 'File Operations', description: 'Create, copy, move, and delete files', category: 'files', requiredLevel: 'beginner' as SkillLevel, steps: [{ instruction: 'Create a new file with touch', hint: 'Try touch newfile.txt', explanation: 'touch creates a new empty file', expectedCommand: 'touch' }], estimatedMinutes: 8 },
  { id: 'git-basics', title: 'Git Fundamentals', description: 'Track changes with git', category: 'git', requiredLevel: 'beginner' as SkillLevel, steps: [{ instruction: 'Check repository status', hint: 'Try git status', explanation: 'git status shows the state of your working tree', expectedCommand: 'git status' }], estimatedMinutes: 15 },
  { id: 'git-branching', title: 'Git Branching', description: 'Work with branches effectively', category: 'git', requiredLevel: 'intermediate' as SkillLevel, steps: [{ instruction: 'Create and switch to a new branch', hint: 'Try git checkout -b feature', explanation: 'Branches let you work on features in isolation', expectedCommand: 'git checkout' }], estimatedMinutes: 12 },
  { id: 'shell-scripting', title: 'Shell Scripting', description: 'Write your first shell script', category: 'scripting', requiredLevel: 'intermediate' as SkillLevel, steps: [{ instruction: 'Create a script file', hint: 'Try echo "#!/bin/bash" > script.sh', explanation: 'Shell scripts automate repetitive tasks', expectedCommand: 'echo' }], estimatedMinutes: 20 },
  { id: 'advanced-git', title: 'Advanced Git Workflows', description: 'Rebase, cherry-pick, and bisect', category: 'git', requiredLevel: 'advanced' as SkillLevel, steps: [{ instruction: 'Start an interactive rebase', hint: 'Try git rebase -i HEAD~3', explanation: 'Interactive rebase lets you edit commit history', expectedCommand: 'git rebase' }], estimatedMinutes: 25 },
  { id: 'perf-profiling', title: 'Performance Profiling', description: 'Profile processes and optimize', category: 'system', requiredLevel: 'advanced' as SkillLevel, steps: [{ instruction: 'View running processes', hint: 'Try htop or top', explanation: 'Process monitors help identify resource hogs', expectedCommand: 'top' }], estimatedMinutes: 15 },
  { id: 'docker-mastery', title: 'Docker Mastery', description: 'Containers and image optimization', category: 'docker', requiredLevel: 'expert' as SkillLevel, steps: [{ instruction: 'Build a multi-stage Docker image', hint: 'Use FROM ... AS builder pattern', explanation: 'Multi-stage builds reduce image size', expectedCommand: 'docker build' }], estimatedMinutes: 30 },
];

function suggestTutorial(skillLevel: SkillLevel, completedIds: string[]): Tutorial | null {
  const levelOrder: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];
  const currentIdx = levelOrder.indexOf(skillLevel);

  // Suggest tutorials at current level or one below
  const eligibleLevels = levelOrder.slice(0, currentIdx + 1);
  const candidates = TUTORIAL_LIBRARY.filter(
    t => eligibleLevels.includes(t.requiredLevel) && !completedIds.includes(t.id)
  );

  if (candidates.length === 0) return null;

  // Prefer tutorials at the user's current level
  const atLevel = candidates.filter(t => t.requiredLevel === skillLevel);
  if (atLevel.length > 0) return atLevel[0]!;
  return candidates[candidates.length - 1]!;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 FACTORY — createAdaptiveWorkspace
// ═══════════════════════════════════════════════════════════════════════════════

export function createAdaptiveWorkspace(config: AdaptiveWorkspaceConfig): AdaptiveWorkspaceHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ── Initialize workspace ──
  const wsResult = createWorkspace({ name: config.workspaceName, theme: config.theme ?? 'dark' });
  if (!wsResult.success) throw new Error(`Workspace creation failed: ${wsResult.code}`);
  let workspaceState: WorkspaceState = wsResult.value;

  // ── Initialize focus theme ──
  let focusState: FocusThemeState = createFocusThemeState();

  // ── Learning state ──
  const learningRate = config.learningRate ?? 0.1;
  const activityLog: string[] = [];
  const commandsUsed = new Set<string>();
  const conceptsLearned = new Set<string>();
  const completedTutorials: string[] = [];
  let currentSkillLevel: SkillLevel = 'beginner';
  let feedbackAccepted = 0;
  let feedbackRejected = 0;
  let feedbackTotal = 0;

  // ── Style accumulator ──
  const styleAccumulator = createStyleAccumulator();

  // ── Skill level assessment ──
  function reassessSkill(): void {
    const uniqueCount = commandsUsed.size;
    if (uniqueCount >= 50 && conceptsLearned.size >= 20) currentSkillLevel = 'expert';
    else if (uniqueCount >= 25 && conceptsLearned.size >= 10) currentSkillLevel = 'advanced';
    else if (uniqueCount >= 10 && conceptsLearned.size >= 4) currentSkillLevel = 'intermediate';
    else currentSkillLevel = 'beginner';
  }

  // ── Methods ──

  function getWorkspace(): WorkspaceState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return workspaceState;
  }

  function recordActivity(action: string): void {
    guardDestroyed();
    activityLog.push(action);
    if (activityLog.length > 1000) activityLog.shift();

    // Extract command from action
    const cmd = action.split(' ')[0]?.toLowerCase() ?? '';
    if (cmd) commandsUsed.add(cmd);

    // Concept detection
    if (/\bgit\b/.test(action)) conceptsLearned.add('version-control');
    if (/\bnpm|pnpm|yarn\b/.test(action)) conceptsLearned.add('package-management');
    if (/\bdocker\b/.test(action)) conceptsLearned.add('containers');
    if (/\bgrep|find|ag|rg\b/.test(action)) conceptsLearned.add('search');
    if (/\bssh|scp\b/.test(action)) conceptsLearned.add('remote-access');
    if (/\bcurl|wget|fetch\b/.test(action)) conceptsLearned.add('networking');

    reassessSkill();
    circuitStats = recordSuccess(circuitStats);
  }

  function getSkillAssessmentValue(): SkillAssessment {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return {
      level: currentSkillLevel,
      commandsUsed: activityLog.length,
      uniqueCommandsUsed: commandsUsed.size,
      conceptsLearned: Array.from(conceptsLearned),
      lastAssessedAt: Date.now(),
    };
  }

  function doEnterFocusMode(intensity?: FocusIntensity): void {
    guardDestroyed();
    const level = intensity ?? 'moderate';
    focusState = enterFocusMode(focusState, {}, level);
    circuitStats = recordSuccess(circuitStats);
  }

  function doExitFocusMode(): void {
    guardDestroyed();
    focusState = exitFocusMode(focusState);
    circuitStats = recordSuccess(circuitStats);
  }

  function doRecordFeedback(signal: 'accepted' | 'rejected', _requestType: string): void {
    guardDestroyed();
    feedbackTotal++;
    if (signal === 'accepted') feedbackAccepted++;
    else feedbackRejected++;
    circuitStats = recordSuccess(circuitStats);
  }

  function getAdaptedSettings(): AdaptedSettings {
    guardDestroyed();
    const acceptRate = feedbackTotal > 0 ? feedbackAccepted / feedbackTotal : 0.5;

    // Adapt confidence floor based on acceptance rate
    const confidenceFloor = Math.max(0.1, Math.min(0.9, 0.5 + (acceptRate - 0.5) * learningRate * 10));

    // Adapt verbosity based on skill level
    let verbosity: AdaptedSettings['verbosity'] = 'standard';
    if (currentSkillLevel === 'beginner') verbosity = 'verbose';
    else if (currentSkillLevel === 'expert') verbosity = 'minimal';

    circuitStats = recordSuccess(circuitStats);
    return { confidenceFloor, verbosity, preferredModel: null };
  }

  function learnFromCode(code: string, _language: string): void {
    guardDestroyed();
    analyzeCodeStyle(styleAccumulator, code);
    circuitStats = recordSuccess(circuitStats);
  }

  function getStyleProfileValue(): StyleProfile {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return computeStyleProfile(styleAccumulator);
  }

  function getRecentActivity(): string[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return activityLog.slice(-50);
  }

  function suggestNextTutorial(): Tutorial | null {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return suggestTutorial(currentSkillLevel, completedTutorials);
  }

  function getCircuitStats(): CircuitStats {
    return { ...circuitStats, uptime: Date.now() - createdAt };
  }

  function getHealth(): CircuitHealthReport {
    const h = destroyed ? 'destroyed' as const : healthFromStats(circuitStats);
    return buildHealthReport('adaptive-workspace', h, formatStatsDetails(circuitStats), createdAt);
  }

  function destroy(): void {
    destroyed = true;
  }

  return Object.freeze({
    name: 'adaptive-workspace',
    getWorkspace,
    recordActivity,
    getSkillAssessment: getSkillAssessmentValue,
    enterFocusMode: doEnterFocusMode,
    exitFocusMode: doExitFocusMode,
    recordFeedback: doRecordFeedback,
    getAdaptedSettings,
    learnFromCode,
    getStyleProfile: getStyleProfileValue,
    getRecentActivity,
    suggestNextTutorial,
    stats: getCircuitStats,
    health: getHealth,
    destroy,
  });
}
